import { GridRowParams } from '@mui/x-data-grid-pro';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { LargeTableEmptyState } from '@/components/tables/components/LargeTableEmptyState/LargeTableEmptyState';
import { MAXIMUM_ROW_COUNT } from '@/components/tables/DataTable/constants';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { GrowthProfileModal } from '@/modules/growthProfiles/GrowthProfileModal/GrowthProfileModal';
import { getNodes } from '@/utils/graphqlUtils';

import { DeleteUsedGrowthProfileModal } from './DeleteUsedGrowthProfileModal/DeleteUsedGrowthProfileModal';
import {
  GrowthProfilesTable_GrowthProfileFragment,
  useGrowthProfilesTableQuery,
} from './graphql/GrowthProfilesTable.generated';
import { RowData } from './GrowthProfilesTable.constants';
import { GrowthProfilesTableProvider } from './GrowthProfilesTable.context';
import { getGrowthProfilesTableColumns } from './GrowthProfilesTable.utils';

export interface GrowthProfileTableProps {
  householdId: string;
  /** Query names to refetch after a profile changes here. */
  refetchQueries: string[];
}

export interface GrowthProfilesModalData {
  growthProfileId?: string | null | undefined;
  isDuplicating?: boolean | null | undefined;
}

export function GrowthProfilesTable({
  householdId,
  refetchQueries,
}: GrowthProfileTableProps) {
  const canEdit = useCanEditHouseholdData();

  const [
    { isModalOpen: isGrowthProfileModalOpen, data: growthProfileModalData },
    { openModal: openGrowthProfileModal, closeModal: closeGrowthProfileModal },
  ] = useModalState<GrowthProfilesModalData>();
  const [
    {
      isModalOpen: isDeleteUsedGrowthProfileModalOpen,
      data: deleteUsedGrowthProfileModalData,
    },
    {
      openModal: openDeleteUsedGrowthProfileModal,
      closeModal: closeDeleteUsedGrowthProfileModal,
    },
  ] = useModalState<GrowthProfilesTable_GrowthProfileFragment>();

  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const { data, loading } = useGrowthProfilesTableQuery({
    variables: {
      where: {
        hasHouseholdWith: [
          {
            id: householdId,
          },
        ],
      },
    },
    onError: (error) => {
      reportError('failed to load growth profiles', error);
      showFeedback('Failed to load growth profiles.  Please try again later');
    },
  });

  const rows = getNodes(data?.growthProfiles);

  const columns = useMemo(
    () =>
      getGrowthProfilesTableColumns({
        openGrowthProfileModal,
        openDeleteUsedGrowthProfileModal,
        canEdit,
        refetchQueries,
      }),
    [
      openDeleteUsedGrowthProfileModal,
      openGrowthProfileModal,
      canEdit,
      refetchQueries,
    ]
  );

  // this _should_ never happen, but it's better to handle a blank case than to leave the user in a broken state
  if (!loading && isEmpty(rows)) {
    return (
      <GrowthProfilesTableProvider growthProfiles={[]}>
        <LargeTableEmptyState
          title="Add growth profiles"
          description="Growth profiles determine the future projections on the estate waterfall"
          ModalComponent={() => null}
          buttonText="Add growth profile"
          hideButtons={!canEdit}
          Button={({ ...props }) => (
            <Button {...props}>Add growth profile</Button>
          )}
        />
      </GrowthProfilesTableProvider>
    );
  }

  return (
    <GrowthProfilesTableProvider growthProfiles={rows}>
      {isGrowthProfileModalOpen && (
        <GrowthProfileModal
          onClose={closeGrowthProfileModal}
          householdId={householdId}
          {...growthProfileModalData}
          refetchQueries={refetchQueries}
        />
      )}
      {isDeleteUsedGrowthProfileModalOpen &&
        deleteUsedGrowthProfileModalData && (
          <DeleteUsedGrowthProfileModal
            growthProfile={deleteUsedGrowthProfileModalData}
            onClose={closeDeleteUsedGrowthProfileModal}
          />
        )}
      <DataTable
        rows={rows}
        columns={columns}
        pageSize={MAXIMUM_ROW_COUNT}
        hideFooterPagination
        pagination={false}
        onRowClick={({ row }: GridRowParams<RowData>) => {
          openGrowthProfileModal({
            growthProfileId: row.id,
          });
        }}
      />
    </GrowthProfilesTableProvider>
  );
}
